// angular
import { DecimalPipe, UpperCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  Component,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatomoTrackClickDirective } from 'ngx-matomo-client';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { LoginDialogComponent } from '@app/core/dialogs/login-dialog/login-dialog.component';
import { DialogService } from '@app/core/shared/services/dialog.service';
import { AuctionCountdownTimerComponent } from '@app/+auctions/shared/components/auction-countdown-timer/auction-countdown-timer.component';
import { AuctionLotSpecsTabComponent } from '../auction-lot-specs-tab/auction-lot-specs-tab.component';
import { AuctionBidsLiveTabComponent } from '../auction-bids-live-tab/auction-bids-live-tab.component';
// services
import { AuctionsService } from '@app/+auctions/shared/services/auctions.service';
import { UserService } from '@app/shared/services/user.service';
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
// types & enums
import { IAuction } from '@app/+auctions/shared/types/auctions.type';
import { ISectionTab } from '@app/core/shared/interfaces/section-tab.interface';
import { Roles } from '@app/shared/enums/roles.enum';
// pipes & utils
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { currencyToSymbol } from '@app/shared/utils/currency-to-symbol.util';
import { environment } from '@environments/environment';

export type AuctionBidTab = 'place-bid' | 'lot-specs' | 'bids-live';

@Component({
  selector: 'pwj-auction-bid-panel',
  standalone: true,
  imports: [
    DecimalPipe,
    UpperCasePipe,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    AuctionCountdownTimerComponent,
    AuctionLotSpecsTabComponent,
    AuctionBidsLiveTabComponent,
    MatomoTrackClickDirective,
    RouterLink,
  ],
  templateUrl: './auction-bid-panel.component.html',
  styleUrl: './auction-bid-panel.component.scss',
})
export class AuctionBidPanelComponent implements OnInit {
  protected readonly currencyToSymbol = currencyToSymbol;

  auction = input.required<IAuction>();
  auctionId = input.required<string>();
  isActive = input<boolean>(false);

  activeTab = signal<AuctionBidTab>('place-bid');
  bidForm!: FormGroup;
  confirmedProcess = signal(false);
  isDisabledSubmitBtn = signal(true);
  formIsSubmitting = signal(false);
  hasAccessToCreateBid = false;
  bidsCount = signal(0);

  quickIncrements = [1000, 5000, 10000];
  tabs: ISectionTab[] = [];

  isUserLoggedIn = false;

  private readonly _fb = inject(FormBuilder);
  private readonly _auctionsService = inject(AuctionsService);
  private readonly _userService = inject(UserService);
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _recaptchaService = inject(RecaptchaService);
  private readonly _dialogService = inject(DialogService);

  constructor() {
    this._recaptchaService.recaptchaValidation('bid_form');
    this.hasAccessToCreateBid = this._hasAccessPipe.transform(Roles.CreateBid);

    effect(() => {
      this.isUserLoggedIn =
        (this._userService.userSignal()?.roles?.length || 0) > 0;
    });

    effect(() => {
      const count = this._auctionsService.auctionParticipators().length;
      this.bidsCount.set(count);
      this.tabs = this.buildTabs(count);
    });

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData = this._recaptchaService.recaptchaData();
        if (captchaData && captchaData.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn.set(true);
        }
      }
    });
  }

  ngOnInit(): void {
    this.initBidForm();
  }

  get currentHighBid(): number {
    const auction = this.auction();
    return auction?.highest_price || auction?.base_price || 0;
  }

  get minimumBid(): number {
    return this.currentHighBid + 1;
  }

  get productDocuments() {
    return this.auction()?.product?.product_documents || [];
  }

  buildTabs(count: number): ISectionTab[] {
    return [
      { tabTitle: 'place-bid' },
      { tabTitle: 'lot-specs' },
      { tabTitle: 'bids-live', tabDataCount: String(count) },
    ];
  }

  onSelectTab(tabTitle: string): void {
    this.activeTab.set(tabTitle as AuctionBidTab);
  }

  getTabLabel(tabTitle: string): string {
    const labels: Record<string, string> = {
      'place-bid': 'auctions.placeBid',
      'lot-specs': 'auctions.lotSpecSheets',
      'bids-live': 'auctions.bidsLive',
    };

    return labels[tabTitle] || tabTitle;
  }

  initBidForm(): void {
    const auction = this.auction();
    const minPrice = Number(
      auction?.highest_price || auction?.base_price || 0
    );

    this.bidForm = this._fb.group({
      bidAmount: [
        null,
        [
          Validators.required,
          Validators.min(minPrice + 1),
          Validators.max(999999999999),
        ],
      ],
    });

    this.bidForm.valueChanges.subscribe(() => {
      this.isDisabledSubmitBtn.set(!this.bidForm.dirty);
    });
  }

  applyQuickIncrement(amount: number): void {
    const currentValue =
      Number(this.bidForm.get('bidAmount')?.value) || this.minimumBid;
    this.bidForm.patchValue({ bidAmount: currentValue + amount });
    this.bidForm.markAsDirty();
    this.isDisabledSubmitBtn.set(false);
  }

  confirmBidProcess(): void {
    this.confirmedProcess.set(true);
  }

  openLoginDialog(): void {
    this._dialogService.open(LoginDialogComponent, {
      width: 'none',
      height: 'none',
    });
  }

  submitBid(): void {
    this.bidForm.markAllAsTouched();

    if (this.bidForm.invalid || !this.bidForm.dirty) {
      return;
    }

    if (!this.confirmedProcess()) {
      this.confirmBidProcess();
      return;
    }

    this.formIsSubmitting.set(true);
    this._auctionsService.postAuctionBid(this.auctionId(), {
      price: Number(this.bidForm.get('bidAmount')?.value),
    });

    this.confirmedProcess.set(false);
    this.formIsSubmitting.set(false);
    this.bidForm.reset();
    this.isDisabledSubmitBtn.set(true);
  }
}
